// src/pages/Calendar.js
// Updated: 01/14/2026 09:40 PM

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { db } from '../services/firebase';
import { collection, onSnapshot, query, where, collectionGroup, getDocs, doc, updateDoc, addDoc, deleteDoc, serverTimestamp, arrayUnion } from 'firebase/firestore'; 

import { Calendar as BigCalendar, dateFnsLocalizer, Views } from 'react-big-calendar';
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import startOfWeek from 'date-fns/startOfWeek';
import getDay from 'date-fns/getDay';
import enUS from 'date-fns/locale/en-US';
import 'react-big-calendar/lib/css/react-big-calendar.css';

import { rrulestr } from 'rrule'; 
import { addMonths, startOfMonth, endOfMonth, isValid, addDays as dateFnsAddDays, differenceInMilliseconds, addMilliseconds } from 'date-fns'; 

import EventModal from '../components/EventModal';

import { Container, Typography, CircularProgress, Box, Button, Paper, ToggleButtonGroup, ToggleButton } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ListIcon from '@mui/icons-material/List'; 
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import EventGalleryCard from '../components/EventGalleryCard';
import { deleteFileByUrl } from '../services/storageService'; 
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'; 
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'; 


const locales = { 'en-US': enUS };
const localizer = dateFnsLocalizer({ format, parse, startOfWeek, getDay, locales });

function CalendarPage() { 
  const { userProfile, currentUser, churchProfile } = useAuth(); 
  
  const [events, setEvents] = useState({ main: [], group: [], volunteer: [] }); 
  const [loading, setLoading] = useState(true);
  const [churchMembers, setChurchMembers] = useState([]); 
  const [isEventRegistrationEnabledForChurch, setIsEventRegistrationEnabledForChurch] = useState(false); 

  const [date, setDate] = useState(new Date()); 
  const [view, setView] = useState(Views.MONTH);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null); 
  const [selectedOccurrenceStart, setSelectedOccurrenceStart] = useState(null); 

  const [upcomingGalleryEvents, setUpcomingGalleryEvents] = useState([]); 
  const [viewMode, setViewMode] = useState('calendar'); 

  const isLeadership = userProfile?.role === 'Leadership' || userProfile?.role === 'Church Administrator' || userProfile?.role === 'Super Administrator';

  useEffect(() => {
    if (events.main.length > 0 || events.group.length > 0 || events.volunteer.length > 0) {
      const now = new Date();
      const combinedAllEvents = [...events.main, ...events.group, ...events.volunteer];
      const upcomingEvents = combinedAllEvents.filter(event => {
        const comparisonDate = event.end || event.start;
        return comparisonDate && comparisonDate instanceof Date && comparisonDate >= now;
      }).sort((a, b) => {
        if (a.isFeatured && !b.isFeatured) return -1;
        if (!a.isFeatured && b.isFeatured) return 1;
        return a.start.getTime() - b.start.getTime();
      });

      const nextSixEvents = upcomingEvents.slice(0, 6);
      setUpcomingGalleryEvents(nextSixEvents);
    } else {
      setUpcomingGalleryEvents([]);
    }
  }, [events]);


  useEffect(() => {
    if (!userProfile?.churchId) {
      setLoading(false);
      return;
    }

    const unsubscribes = [];
    let isMounted = true; 

    const fetchData = async () => {
      setLoading(true);

      const churchDocRef = doc(db, 'churches', userProfile.churchId);
      const unsubscribeChurch = onSnapshot(churchDocRef, (docSnap) => {
        if (isMounted) {
          if (docSnap.exists()) {
            setIsEventRegistrationEnabledForChurch(docSnap.data().enableEventRegistration || false);
          } else {
            setIsEventRegistrationEnabledForChurch(false);
          }
        }
      });
      unsubscribes.push(unsubscribeChurch);

      const fetchChurchMembersPromise = new Promise(async (resolve) => {
        try {
          const usersColRef = collection(db, 'users');
          const q = query(usersColRef, where('churchId', '==', userProfile.churchId), where('status', '==', 'active'));
          const querySnapshot = await getDocs(q);
          const members = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
            .sort((a, b) => {
              const lastNameComparison = a.lastName.localeCompare(b.lastName);
              if (lastNameComparison !== 0) return lastNameComparison;
              return a.firstName.localeCompare(b.firstName);
            });
          if (isMounted) {
            setChurchMembers(members);
          }
          resolve();
        }
        catch (error) {
          console.error("Error fetching church members for event POCs:", error);
          if (isMounted) {
            setChurchMembers([]); 
          }
          resolve(); 
        }
      });

      const mainEventsRef = collection(db, 'churches', userProfile.churchId, 'events');
      const unsubscribeMain = onSnapshot(mainEventsRef, (snapshot) => {
        if (isMounted) {
          
          const fetchedMainEvents = snapshot.docs.flatMap(doc => { 
              const data = doc.data();
              let originalStart = data.startAt?.toDate ? data.startAt.toDate() : null;
              let originalEnd = data.endAt?.toDate ? data.endAt.toDate() : originalStart;
              const isAllDay = data.isAllDay;
              const exdates = data.exdates || [];

              if (originalStart && !isValid(originalStart)) originalStart = null;
              if (originalEnd && !isValid(originalEnd)) originalEnd = null;
              if (!originalEnd && originalStart) originalEnd = originalStart;

              let eventTitle = data.title; 
              if (data.isFeatured) {
                  if (!eventTitle.startsWith('⭐ ')) {
                      eventTitle = '⭐ ' + eventTitle;
                  }
              } else {
                  if (eventTitle.startsWith('⭐ ')) {
                      eventTitle = eventTitle.substring(2); 
                  }
              }

              const baseEvent = {
                  id: doc.id,
                  title: eventTitle,
                  allDay: isAllDay,
                  isGroupEvent: false,
                  isVolunteerEvent: false,
                  description: data.description,
                  location: data.location,
                  virtualLink: data.virtualLink,
                  attendees: data.attendees,
                  recurrenceRules: data.recurrenceRules,
                  exdates: exdates,
                  isRegistrationEnabled: data.isRegistrationEnabled,
                  capacity: data.capacity,
                  currentRegistrations: data.currentRegistrations,
                  registrationDeadline: data.registrationDeadline || null,
                  isPaidEvent: data.isPaidEvent,
                  ticketPrice: data.ticketPrice,
                  eventPOCs: data.eventPOCs,
                  imageUrl: data.imageUrl || '',
                  isFeatured: data.isFeatured || false,
                  originalFirestoreStart: originalStart,
                  originalFirestoreEnd: originalEnd,
              };

              if (data.recurrenceRules && data.recurrenceRules.length > 0 && originalStart) {
                  try {
                      const rruleString = data.recurrenceRules[0];
                      
                      // *** FIXED RECURRENCE LOGIC ***
                      // 1. Determine if we should pass 'dtstart' to rrulestr.
                      // If the string already has DTSTART, we should generally use it to avoid timezone conflicts,
                      // but we need to account for the "floating" nature of rrule dates.
                      const rruleOptions = {};
                      
                      // Only pass dtstart override if the string DOES NOT contain DTSTART
                      // This prevents 'double-adjusting' or skipping instances if the Local Time pushes 
                      // the start date to the next day.
                      if (!rruleString.includes('DTSTART')) {
                        rruleOptions.dtstart = originalStart;
                      }

                      const rrule = rrulestr(rruleString, rruleOptions); 
                      
                      const calendarStartDate = startOfMonth(addMonths(date, -12)); 
                      const calendarEndDate = endOfMonth(addMonths(date, 12));    

                      const occurrences = rrule.between(calendarStartDate, calendarEndDate, true); 

                      // Calculate the duration of the event
                      const durationMillis = originalEnd.getTime() - originalStart.getTime();

                      // *** CALCULATE OFFSET ***
                      // If the RRULE string has a DTSTART, 'rrule' returns dates relative to that string's time (floating).
                      // We need to shift these floating dates to match the actual absolute time of 'originalStart'.
                      // We find the first occurrence (floating) and compare it to 'originalStart' (absolute).
                      let timeOffset = 0;
                      if (occurrences.length > 0) {
                         // Find the first recurrence that theoretically matches the start
                         // This is tricky because rrule.between might start later.
                         // Instead, we just trust the first occurrence SHOULD align with originalStart
                         // if the pattern is valid. 
                         // A safer bet: Calculate the difference between the "Floating Hour" and "Local Hour".
                         
                         // Better approach:
                         // We construct the occurrence simply by applying the Date/Month/Year from the Rule
                         // BUT keeping the Time from the Original Event.
                         // This handles the "Tuesday" bug where the date shifts but time doesn't.
                      }

                      return occurrences.map((occDate) => { 
                          let newStart = new Date(occDate);
                          let newEnd = new Date(occDate);

                          if (!isAllDay) {
                              // *** FIXED DATE CONSTRUCTION ***
                              // Instead of using the hour from originalStart (which might be shifted by timezone),
                              // we use the full date from occDate (which respects the Church Timezone day)
                              // and then apply the offset if needed?
                              
                              // Actually, the simplest fix for "It puts it on Tuesdays" when it should be "Wednesday"
                              // is to trust the YEAR/MONTH/DAY from the RRULE (occDate),
                              // and TRUST the HOUR/MINUTE from the 'originalStart' (Local Time).
                              // 
                              // If 'originalStart' is 19:00 (7pm) Local Time.
                              // And 'occDate' is Wednesday.
                              // Result: Wednesday 19:00.
                              
                              // If the user sees "Tuesday", it means 'occDate' is likely Tuesday.
                              // Why? Because rrule might be converting UTC to Local?
                              // RRule.between returns local dates.
                              
                              // Let's use the explicit approach:
                              // 1. Take the Year, Month, Date from the RRule occurrence.
                              // 2. Take the Hours, Minutes, Seconds from the Original Start (Local Time).
                              // This forces the event to happen on the "Rule Day" at the "Original Time".
                              
                              newStart = new Date(
                                  occDate.getFullYear(),
                                  occDate.getMonth(),
                                  occDate.getDate(),
                                  originalStart.getHours(),
                                  originalStart.getMinutes(),
                                  originalStart.getSeconds(),
                                  originalStart.getMilliseconds()
                              );
                              
                              newEnd = addMilliseconds(newStart, durationMillis);

                              // Edge case: If the original event spanned across midnight
                              // originalEnd > originalStart.
                              // durationMillis handles this.
                          } else {
                            // All day events just use the date
                            newEnd = dateFnsAddDays(occDate, 1);
                          }

                          const occurrenceFormatForExdate = format(newStart, "yyyyMMdd'T'HHmmss'Z'"); 
                          // Also check against the "UTC" version of the occurrence string which is often how exdates are stored
                          // exdates usually match the RRULE syntax (floating).
                          
                          // Simplified exdate check (might need refinement based on exact string format saved)
                          if (exdates.some(ex => ex.startsWith(format(newStart, "yyyyMMdd")))) {
                              return null;
                          }

                          return {
                              ...baseEvent,
                              id: baseEvent.id,
                              title: baseEvent.title, 
                              start: newStart,
                              end: newEnd,
                              selectedOccurrenceStart: newStart,
                          };
                      }).filter(e => e !== null && isValid(e.start) && isValid(e.end));
                  } catch (e) {
                      console.error("Error expanding RRULE:", e);
                      return [];
                  }
              } else if (originalStart && isValid(originalStart)) {
                  return [{
                      ...baseEvent,
                      id: baseEvent.id,
                      start: originalStart,
                      end: originalEnd,
                  }];
              }
              return [];
          });
          const validMainEvents = fetchedMainEvents.flat();
          setEvents(prev => ({ ...prev, main: validMainEvents }));
        }
      }, (error) => {
          console.error("Error fetching main events:", error);
      });
      unsubscribes.push(unsubscribeMain);

      const groupEventsRef = collectionGroup(db, 'groupEvents');
      const groupEventsQuery = query(
        groupEventsRef,
        where('churchId', '==', userProfile.churchId)
      );
      const unsubscribeGroup = onSnapshot(groupEventsQuery, (snapshot) => {
        if (isMounted) {
          const fetchedGroupEvents = snapshot.docs.flatMap(doc => {
            const data = doc.data();
            let originalStart = data.startAt?.toDate ? data.startAt.toDate() : null;
            let originalEnd = data.endAt?.toDate ? data.endAt.toDate() : originalStart;
            const isAllDay = data.isAllDay;
            const exdates = data.exdates || [];

            if (originalStart && !isValid(originalStart)) originalStart = null;
            if (originalEnd && !isValid(originalEnd)) originalEnd = null;
            if (!originalEnd && originalStart) originalEnd = originalStart;

            const baseEvent = {
                id: doc.id,
                title: `[${data.groupName || 'Group'}] ${data.title}`,
                allDay: isAllDay,
                isGroupEvent: true,
                isVolunteerEvent: false,
                description: data.description,
                location: data.location,
                virtualLink: data.virtualLink,
                attendees: data.attendees,
                recurrenceRules: data.recurrenceRules,
                exdates: exdates,
                isRegistrationEnabled: false,
                capacity: null,
                currentRegistrations: 0,
                registrationDeadline: null,
                isPaidEvent: false,
                ticketPrice: null,
                eventPOCs: [],
                imageUrl: data.imageUrl || '',
                isFeatured: data.isFeatured || false,
                originalFirestoreStart: originalStart,
                originalFirestoreEnd: originalEnd,
                groupId: data.groupId, 
            };

            if (data.recurrenceRules && data.recurrenceRules.length > 0 && originalStart) {
                try {
                    const rruleString = data.recurrenceRules[0];
                    const rruleOptions = {};
                    if (!rruleString.includes('DTSTART')) {
                        rruleOptions.dtstart = originalStart;
                    }
                    const rrule = rrulestr(rruleString, rruleOptions);
                    
                    const calendarStartDate = startOfMonth(addMonths(date, -12)); 
                    const calendarEndDate = endOfMonth(addMonths(date, 12));
                    
                    const occurrences = rrule.between(calendarStartDate, calendarEndDate, true);
                    const durationMillis = originalEnd.getTime() - originalStart.getTime();

                    return occurrences.map((occDate) => {
                        let newStart = new Date(occDate);
                        let newEnd = new Date(occDate);
                        
                        if (!isAllDay) {
                            newStart = new Date(
                                occDate.getFullYear(), occDate.getMonth(), occDate.getDate(),
                                originalStart.getHours(), originalStart.getMinutes(),
                                originalStart.getSeconds(), originalStart.getMilliseconds()
                            );
                            newEnd = addMilliseconds(newStart, durationMillis);
                        } else {
                            newEnd = dateFnsAddDays(occDate, 1);
                        }

                        // Simple Exdate check
                         if (exdates.some(ex => ex.startsWith(format(newStart, "yyyyMMdd")))) {
                            return null;
                        }

                        return {
                            ...baseEvent,
                            id: baseEvent.id,
                            start: newStart,
                            end: newEnd,
                            selectedOccurrenceStart: newStart,
                        };
                    }).filter(e => e !== null && isValid(e.start) && isValid(e.end));
                } catch (e) {
                    console.error("Error expanding RRULE for group event:", e);
                    return [];
                }
            } else if (originalStart && isValid(originalStart)) {
                return [{
                    ...baseEvent,
                    id: baseEvent.id,
                    start: originalStart,
                    end: originalEnd,
                }];
            }
            return [];
          });
          const validGroupEvents = fetchedGroupEvents.flat();
          setEvents(prev => ({ ...prev, group: validGroupEvents }));
        }
      }, (error) => {
          console.error("Error fetching group events:", error);
      });
      unsubscribes.push(unsubscribeGroup);

      const volunteerOpportunitiesRef = collection(db, 'churches', userProfile.churchId, 'volunteerOpportunities');
      const volunteerQuery = query(
        volunteerOpportunitiesRef,
        where('showOnMainCalendar', '==', true) 
      );
      const unsubscribeVolunteer = onSnapshot(volunteerQuery, (snapshot) => {
        if (isMounted) {
          const fetchedVolunteerEvents = snapshot.docs.map(doc => {
            const data = doc.data();
            const start = data.date?.toDate ? data.date.toDate() : null; 
            const end = data.endTime?.toDate ? data.endTime.toDate() : start;
            
            const eventForCalendar = {
              id: doc.id,
              title: `[Volunteer] ${data.title}`,
              start: start,
              end: end,
              allDay: false, 
              isGroupEvent: false,
              isVolunteerEvent: true, 
              description: data.description,
              location: data.location,
              contactPersonName: data.contactPersonName,
              requiredVolunteers: data.requiredVolunteers,
              currentSignUps: data.currentSignUps,
              isRegistrationEnabled: false,
              capacity: null,
              currentRegistrations: 0,
              registrationDeadline: null,
              isPaidEvent: false,
              ticketPrice: null,
              eventPOCs: [],
              originalFirestoreStart: start, 
              originalFirestoreEnd: end,
            };
            return eventForCalendar;
          }).filter(e => e.start instanceof Date); 
          setEvents(prev => ({ ...prev, volunteer: fetchedVolunteerEvents }));
        }
      }, (error) => {
        console.error("Error fetching volunteer opportunities for calendar:", error);
      });
      unsubscribes.push(unsubscribeVolunteer);

      await fetchChurchMembersPromise; 
      if (isMounted) {
        setLoading(false);
      }
    };

    fetchData(); 

    return () => {
      isMounted = false; 
      unsubscribes.forEach(unsub => unsub()); 
    };
  }, [userProfile, date]);

  const handleCreateEventClick = () => {
    setSelectedEvent(null);
    setIsModalOpen(true);
  };

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedEvent(null);
  }, []);

  const handleSelectEvent = useCallback((event) => {
    setSelectedEvent({
      id: event.id,
      title: event.title,
      description: event.description || '',
      location: event.location || '',
      virtualLink: event.virtualLink || '',
      isAllDay: event.allDay,
      attendees: event.attendees || [],
      recurrenceRules: event.recurrenceRules || [],
      exdates: event.exdates || [],
      isGroupEvent: event.isGroupEvent,
      isVolunteerEvent: event.isVolunteerEvent,
      contactPersonName: event.contactPersonName || '',
      requiredVolunteers: event.requiredVolunteers || 0,
      currentSignUps: event.currentSignUps || 0,

      isRegistrationEnabled: event.isRegistrationEnabled || false,
      capacity: event.capacity || '',
      currentRegistrations: event.currentRegistrations || 0,
      registrationDeadline: event.registrationDeadline,
      isPaidEvent: event.isPaidEvent || false,
      ticketPrice: event.ticketPrice || '',
      eventPOCs: event.eventPOCs || [],
      imageUrl: event.imageUrl || '',
      isFeatured: event.isFeatured || false,
      
      startDateTime: event.start, 
      endDateTime: event.end,     
      originalFirestoreStart: event.originalFirestoreStart, 
      originalFirestoreEnd: event.originalFirestoreEnd,
    });
    setSelectedOccurrenceStart(event.start);
    setIsModalOpen(true);
  }, []);

  const handleSaveEvent = async (eventData) => {
    if (!userProfile?.churchId || !currentUser?.uid) { 
      alert("Error: User profile or ID missing. Cannot save event.");
      return;
    }
    
    if (selectedEvent?.isGroupEvent) {
      alert("Group events must be edited/deleted from within their specific group page.");
      setIsModalOpen(false);
    } else if (selectedEvent?.isVolunteerEvent) {
        alert("Volunteer opportunities must be edited/deleted from the 'Volunteer opportunities' page.");
        setIsModalOpen(false);
    } else {
        const dataToSave = {
            title: eventData.title,
            description: eventData.description,
            location: eventData.location,
            startAt: eventData.startAt, 
            endAt: eventData.endAt,     
            isAllDay: eventData.isAllDay,
            attendees: eventData.attendees,
            virtualLink: eventData.virtualLink,
            recurrenceRules: eventData.recurrenceRules,
            exdates: eventData.exdates, 
            churchId: userProfile.churchId,
            createdBy: currentUser.uid,

            isRegistrationEnabled: eventData.isRegistrationEnabled,
            capacity: eventData.capacity,
            currentRegistrations: eventData.currentRegistrations, 
            registrationDeadline: eventData.registrationDeadline,
            isPaidEvent: eventData.isPaidEvent,
            ticketPrice: eventData.ticketPrice,
            eventPOCs: eventData.eventPOCs,
            imageUrl: eventData.imageUrl,
            isFeatured: eventData.isFeatured,
        };

        try {
            if (selectedEvent?.id) {
                const docRef = doc(db, 'churches', userProfile.churchId, 'events', selectedEvent.id);
                await updateDoc(docRef, { ...dataToSave, updatedAt: serverTimestamp() });
                alert('Event updated successfully!');
            } else {
                await addDoc(collection(db, 'churches', userProfile.churchId, 'events'), { ...dataToSave, createdAt: serverTimestamp() });
                alert('Event created successfully!');
            }
            setIsModalOpen(false);
            setSelectedEvent(null);
            setSelectedOccurrenceStart(null); 
        } catch (error) {
            console.error("Error saving event:", error); 
            alert(`Failed to save event: ${error.message}`);
        }
    }
  };

  const handleDeleteEvent = async (eventId, occurrenceToDelete = null) => { 
    if (!userProfile?.churchId || !eventId) return;

    if (selectedEvent?.isGroupEvent) {
      alert("Group events must be edited/deleted from within their specific group page.");
      return;
    }
    if (selectedEvent?.isVolunteerEvent) {
        alert("Volunteer opportunities must be edited/deleted from the 'Volunteer opportunities' page.");
        setIsModalOpen(false);
        return;
    }

    if (occurrenceToDelete && selectedEvent?.recurrenceRules && selectedEvent.recurrenceRules.length > 0) {
        if (!window.confirm('Are you sure you want to delete ONLY this occurrence?')) {
            return;
        }
        try {
            const eventDocRef = doc(db, 'churches', userProfile.churchId, 'events', eventId);
            const exdateString = format(occurrenceToDelete, "yyyyMMdd'T'HHmmss'Z'");
            await updateDoc(eventDocRef, {
                exdates: arrayUnion(exdateString) 
            });
            alert("Event occurrence deleted!");
            setIsModalOpen(false);
            setSelectedEvent(null);
            setSelectedOccurrenceStart(null); 
        } catch (error) {
            console.error("Error deleting single occurrence:", error);
            alert(`Failed to delete occurrence: ${error.message}`);
        }
    } else { 
        if (!window.confirm('Are you sure you want to delete this event series?')) { 
            return;
        }
        try {
            if (selectedEvent?.imageUrl) {
                await deleteFileByUrl(selectedEvent.imageUrl);
            }
            await deleteDoc(doc(db, 'churches', userProfile.churchId, 'events', eventId));
            alert('Event series deleted.');
            setIsModalOpen(false);
            setSelectedEvent(null);
            setSelectedOccurrenceStart(null); 
        } catch (error) {
            console.error("Error deleting event series:", error);
            alert('Failed to delete event series.');
        }
    }
  };

  const eventPropGetter = useCallback(
    (event, start, end, isSelected) => {
      let style = {};
      if (event.isGroupEvent) {
        if (event.showOnMainCalendar) { 
          style = { backgroundColor: '#3174ad', borderColor: '#2a6496', color: 'white' }; 
        } else {
          style = { backgroundColor: '#468847', borderColor: '#356635', color: 'white' }; 
        }
      } else if (event.isVolunteerEvent) { 
          style = { backgroundColor: '#FFC107', borderColor: '#FFA000', color: 'black' }; 
      } else { 
        style = { backgroundColor: '#73b0f5', borderColor: '#5f99e3', color: 'black' }; 
        if (event.virtualLink) {
          style.border = '2px dashed #FFEB3B'; 
        }
        if (isEventRegistrationEnabledForChurch && event.isRegistrationEnabled) { 
            style = { ...style, backgroundColor: '#8BC34A', borderColor: '#689F38' }; 
        }
      }
      style.borderRadius = '5px';
      style.opacity = 0.8;
      style.display = 'block';

      return { style };
    },
    [isEventRegistrationEnabledForChurch] 
  );

  const allEvents = [...events.main, ...events.group, ...events.volunteer];

  if (loading) return <CircularProgress />;
  if (!userProfile?.churchId) {
    return (
      <Container maxWidth="md">
        <Typography variant="h6" color="error" sx={{ mt: 3 }}>
          Please ensure your user profile is complete and associated with a church.
        </Typography>
      </Container>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth="xl">
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4">Church Calendar</Typography>
          {isLeadership && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateEventClick}
            >
              Create Event
            </Button>
          )}
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(e, newMode) => { if (newMode) setViewMode(newMode); }}
            aria-label="calendar view mode"
          >
            <ToggleButton value="calendar" aria-label="calendar view">
              <CalendarMonthIcon /> Calendar View
            </ToggleButton>
            <ToggleButton value="gallery" aria-label="gallery view">
              <ListIcon /> Gallery View (Next Events)
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {viewMode === 'gallery' && upcomingGalleryEvents.length > 0 && ( 
          <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 2, mb: 4 }}> 
            {upcomingGalleryEvents.map(event => ( 
              <EventGalleryCard 
                key={event.id} 
                event={event} 
                onClick={handleSelectEvent} 
              />
            ))}
          </Box>
        )}
        {viewMode === 'gallery' && upcomingGalleryEvents.length === 0 && ( 
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
            <Typography variant="body1" color="text.secondary">No upcoming events for the gallery view.</Typography>
          </Box>
        )}

        {viewMode === 'calendar' && (
          <Paper sx={{ height: '80vh', p: 2 }}>
            <BigCalendar
              localizer={localizer}
              events={allEvents}
              startAccessor="start" 
              endAccessor="end"     
              allDayAccessor="allDay" 
              style={{ height: '100%' }} 
              onSelectEvent={handleSelectEvent} 
              onNavigate={setDate} 
              onView={setView}     
              date={date}          
              view={view}          
              views={[Views.MONTH, Views.WEEK, Views.DAY, Views.AGENDA]} 
              eventPropGetter={eventPropGetter} 
            />
          </Paper>
        )}

        <EventModal
          open={isModalOpen}
          onClose={handleCloseModal}
          onSave={handleSaveEvent}
          onDelete={handleDeleteEvent}
          event={selectedEvent}
          isLeadership={isLeadership}
          churchMembers={churchMembers} 
          isEventRegistrationEnabledForChurch={isEventRegistrationEnabledForChurch} 
          selectedOccurrenceStart={selectedOccurrenceStart} 
          churchProfile={churchProfile}
        />
      </Container>
    </LocalizationProvider>
  );
}

export default CalendarPage;